import React, { useRef, useState, useEffect, useMemo } from "react";
import { Pause, MessageSquarePlus, Check, Trash2, Play, Reply, X, Clock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

function formatMMSS(seconds) {
    const s = Math.floor(seconds || 0);
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

const ROLE_STYLES = {
    admin: { label: "Social Factory", chip: "bg-[#E5A93D]/20 text-[#E5A93D]" },
    marketing_manager: { label: "Marketing", chip: "bg-[#7C3AED]/20 text-[#7C3AED]" },
    agent: { label: "Makelaar", chip: "bg-emerald-500/20 text-emerald-600" },
};

export default function CommentablePreview({ videoUrl, requestId, currentUser, readOnly = false, assetType = "preview" }) {
    const videoRef = useRef(null);
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    const src = videoUrl.startsWith("http") ? videoUrl : `${BACKEND}${videoUrl}`;

    const [comments, setComments] = useState([]);
    const [pausedAt, setPausedAt] = useState(null);
    const [newText, setNewText] = useState("");
    const [posting, setPosting] = useState(false);
    const [duration, setDuration] = useState(0);
    const [replyTo, setReplyTo] = useState(null); // comment object being replied to
    const [replyText, setReplyText] = useState("");
    const [replyPosting, setReplyPosting] = useState(false);

    const fetchComments = async () => {
        try {
            const { data } = await api.get(`/requests/${requestId}/comments`);
            // Filter by asset_type if backend stamped them; legacy comments without asset_type default to "preview"
            setComments(data.filter((c) => (c.asset_type || "preview") === assetType));
        } catch {}
    };

    useEffect(() => { fetchComments(); /* eslint-disable-next-line */ }, [requestId, assetType]);

    // Group: top-level comments + replies
    const { topLevel, repliesByParent } = useMemo(() => {
        const top = [];
        const repls = {};
        comments.forEach((c) => {
            if (c.parent_id) {
                repls[c.parent_id] = repls[c.parent_id] || [];
                repls[c.parent_id].push(c);
            } else {
                top.push(c);
            }
        });
        // Sort replies by created_at asc
        Object.keys(repls).forEach((k) => repls[k].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));
        // Sort top-level by timestamp asc
        top.sort((a, b) => a.timestamp_seconds - b.timestamp_seconds);
        return { topLevel: top, repliesByParent: repls };
    }, [comments]);

    const onPause = () => {
        if (!videoRef.current || readOnly) return;
        const t = videoRef.current.currentTime;
        const d = videoRef.current.duration;
        if (t < 0.2 || (d && d - t < 0.2)) return;
        setPausedAt(t);
    };
    const onPlay = () => setPausedAt(null);
    const onLoaded = () => setDuration(videoRef.current?.duration || 0);
    const seekTo = (t) => {
        if (!videoRef.current) return;
        videoRef.current.currentTime = t;
        videoRef.current.pause();
        setPausedAt(t);
    };

    const submitComment = async () => {
        if (!newText.trim() || pausedAt === null) return;
        setPosting(true);
        try {
            await api.post(`/requests/${requestId}/comments`, {
                timestamp_seconds: pausedAt,
                text: newText.trim(),
                asset_type: assetType,
            });
            setNewText("");
            setPausedAt(null);
            await fetchComments();
            toast.success("Opmerking verstuurd");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fout");
        }
        setPosting(false);
    };

    const submitReply = async () => {
        if (!replyText.trim() || !replyTo) return;
        setReplyPosting(true);
        try {
            await api.post(`/requests/${requestId}/comments`, {
                timestamp_seconds: replyTo.timestamp_seconds,
                text: replyText.trim(),
                parent_id: replyTo.id,
                asset_type: assetType,
            });
            setReplyText("");
            setReplyTo(null);
            await fetchComments();
            toast.success("Antwoord geplaatst");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fout");
        }
        setReplyPosting(false);
    };

    const deleteComment = async (cid) => {
        try {
            await api.delete(`/requests/${requestId}/comments/${cid}`);
            await fetchComments();
        } catch {}
    };

    const resolveComment = async (cid) => {
        try {
            await api.patch(`/requests/${requestId}/comments/${cid}/resolve`);
            await fetchComments();
        } catch {}
    };

    const openCount = topLevel.filter((c) => !c.resolved).length;

    return (
        <div data-testid="commentable-preview">
            <div className="relative">
                <video
                    ref={videoRef}
                    src={src}
                    controls
                    onPause={onPause}
                    onPlay={onPlay}
                    onLoadedMetadata={onLoaded}
                    className="w-full aspect-video bg-zinc-50 rounded-sm border border-zinc-200"
                    data-testid="preview-video-player"
                />
                {duration > 0 && topLevel.length > 0 && (
                    <div className="absolute left-0 right-0 bottom-[42px] h-1 pointer-events-none px-2">
                        <div className="relative h-full">
                            {topLevel.map((c) => (
                                <button
                                    key={c.id}
                                    onClick={() => seekTo(c.timestamp_seconds)}
                                    className={`absolute top-0 h-full w-1 pointer-events-auto ${c.resolved ? "bg-emerald-500/60" : "bg-[#E5A93D]"} hover:scale-y-[3] transition-transform`}
                                    style={{ left: `${(c.timestamp_seconds / duration) * 100}%` }}
                                    title={`${formatMMSS(c.timestamp_seconds)} — ${c.user_name}`}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Add comment on pause */}
            {pausedAt !== null && !readOnly && (
                <div className="mt-4 p-4 bg-[#E5A93D]/10 border border-[#E5A93D]/40 rounded-md" data-testid="add-comment-card">
                    <div className="flex items-center gap-2 mb-3 text-[#E5A93D]">
                        <Pause className="h-4 w-4" />
                        <span className="text-xs uppercase tracking-wider font-semibold">Opmerking op {formatMMSS(pausedAt)}</span>
                    </div>
                    <textarea
                        value={newText}
                        onChange={(e) => setNewText(e.target.value)}
                        rows={3}
                        placeholder="Wat moet er aangepast worden op dit punt?..."
                        className="w-full bg-[#FAFAFA] border border-zinc-200 rounded-sm p-3 text-sm text-zinc-800 focus:outline-none focus:border-[#E5A93D]"
                        data-testid="add-comment-textarea"
                    />
                    <div className="flex items-center justify-between mt-3">
                        <button onClick={() => { setPausedAt(null); setNewText(""); }} className="text-sm text-zinc-500 hover:text-zinc-950">Annuleer</button>
                        <button
                            onClick={submitComment}
                            disabled={posting || !newText.trim()}
                            className="flex items-center gap-2 px-4 py-2 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-sm rounded-sm disabled:opacity-50"
                            data-testid="submit-comment-btn"
                        >
                            <MessageSquarePlus className="h-4 w-4" />
                            {posting ? "Versturen..." : "Plaats opmerking"}
                        </button>
                    </div>
                </div>
            )}

            {/* Comments list */}
            <div className="mt-6">
                <div className="flex items-center gap-2 mb-4">
                    <MessageSquarePlus className="h-4 w-4 text-[#E5A93D]" />
                    <h3 className="font-display font-bold text-lg tracking-tight">Opmerkingen</h3>
                    {openCount > 0 && <span className="text-xs bg-[#E5A93D] text-black font-bold px-2 py-0.5 rounded-sm">{openCount} open</span>}
                </div>
                {!readOnly && (
                    <p className="text-xs text-zinc-500 mb-4">
                        <kbd className="px-1.5 py-0.5 bg-zinc-100 border border-zinc-200 rounded-sm text-zinc-600">Pauzeer</kbd> de video om een opmerking te plaatsen op dat moment.
                    </p>
                )}
                {topLevel.length === 0 ? (
                    <div className="text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-200 rounded-md">Nog geen opmerkingen</div>
                ) : (
                    <div className="space-y-3">
                        {topLevel.map((c) => (
                            <div key={c.id} data-testid={`comment-thread-${c.id}`}>
                                <CommentBubble
                                    comment={c}
                                    currentUser={currentUser}
                                    onSeek={seekTo}
                                    onDelete={deleteComment}
                                    onResolve={resolveComment}
                                    onReply={() => { setReplyTo(c); setReplyText(""); }}
                                    isReplyOpen={replyTo?.id === c.id}
                                />
                                {/* Replies */}
                                {(repliesByParent[c.id] || []).map((r) => (
                                    <div key={r.id} className="mt-2 ml-8" data-testid={`reply-${r.id}`}>
                                        <CommentBubble
                                            comment={r}
                                            currentUser={currentUser}
                                            onSeek={seekTo}
                                            onDelete={deleteComment}
                                            isReply
                                        />
                                    </div>
                                ))}
                                {/* Reply input */}
                                {replyTo?.id === c.id && (
                                    <div className="mt-2 ml-8 p-3 bg-[#FFFFFF] border border-zinc-200 rounded-sm" data-testid={`reply-input-${c.id}`}>
                                        <textarea
                                            autoFocus
                                            value={replyText}
                                            onChange={(e) => setReplyText(e.target.value)}
                                            rows={2}
                                            placeholder={`Antwoord aan ${c.user_name}...`}
                                            className="w-full bg-[#FAFAFA] border border-zinc-200 rounded-sm p-2 text-sm text-zinc-800 focus:outline-none focus:border-[#E5A93D]"
                                        />
                                        <div className="flex items-center justify-end gap-2 mt-2">
                                            <button onClick={() => { setReplyTo(null); setReplyText(""); }} className="text-xs text-zinc-500 hover:text-zinc-950">Annuleer</button>
                                            <button
                                                onClick={submitReply}
                                                disabled={replyPosting || !replyText.trim()}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-xs rounded-sm disabled:opacity-50"
                                                data-testid={`submit-reply-${c.id}`}
                                            >
                                                <Reply className="h-3 w-3" />
                                                {replyPosting ? "..." : "Antwoord"}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function CommentBubble({ comment, currentUser, onSeek, onDelete, onResolve, onReply, isReplyOpen, isReply = false }) {
    const role = comment.user_role || "agent";
    const style = ROLE_STYLES[role] || ROLE_STYLES.agent;
    const canDelete = currentUser && (currentUser.role === "admin" || currentUser.id === comment.user_id);
    const canReply = !!currentUser && !isReply && onReply;
    return (
        <div className={`p-3 rounded-md border ${comment.resolved ? "bg-[#FFFFFF] border-zinc-100 opacity-70" : "bg-[#F4F4F5] border-zinc-200"}`}>
            <div className="flex items-start gap-3">
                {!isReply && (
                    <button
                        onClick={() => onSeek(comment.timestamp_seconds)}
                        className="font-mono text-xs bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold px-2 py-1 rounded-sm flex items-center gap-1 flex-shrink-0 active:scale-[0.98]"
                        title="Spring naar dit moment"
                    >
                        <Play className="h-3 w-3" fill="currentColor" />
                        {formatMMSS(comment.timestamp_seconds)}
                    </button>
                )}
                {isReply && <Reply className="h-3 w-3 text-zinc-500 mt-1 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-medium text-zinc-950">{comment.user_name}</span>
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 rounded-sm ${style.chip}`}>{style.label}</span>
                        <span className="inline-flex items-center gap-1 text-xs text-zinc-500" title={new Date(comment.created_at).toLocaleString("nl-BE")}>
                            <Clock className="h-3 w-3" />
                            {new Date(comment.created_at).toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric" })}
                            <span className="text-zinc-400">•</span>
                            {new Date(comment.created_at).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                        {comment.resolved && <span className="text-[10px] uppercase tracking-wider bg-emerald-500/20 text-emerald-600 px-1.5 rounded-sm">Opgelost</span>}
                    </div>
                    <p className="text-sm text-zinc-800 whitespace-pre-wrap break-words">{comment.text}</p>
                    <div className="flex items-center gap-3 mt-2">
                        {canReply && (
                            <button onClick={onReply} className="text-xs text-zinc-500 hover:text-[#E5A93D] flex items-center gap-1" data-testid={`reply-btn-${comment.id}`}>
                                {isReplyOpen ? <X className="h-3 w-3" /> : <Reply className="h-3 w-3" />}
                                {isReplyOpen ? "Annuleer" : "Antwoord"}
                            </button>
                        )}
                        {!comment.resolved && !isReply && currentUser?.role === "admin" && onResolve && (
                            <button onClick={() => onResolve(comment.id)} className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1">
                                <Check className="h-3 w-3" /> Opgelost
                            </button>
                        )}
                        {canDelete && (
                            <button onClick={() => onDelete(comment.id)} className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1">
                                <Trash2 className="h-3 w-3" /> Verwijder
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
